import { ConfigConstants } from '../../config/config-constants';
import { ModelOptions } from './model-options';

/**
 * Configure the behaviour of a TextModel
 */
export class TextModelOptions extends ModelOptions {
    public readonly minLength: number;
    public readonly maxLength: number;
    public readonly placeholder: string;
    public readonly pattern: string;
    public readonly patternScan: string;
    public readonly required: boolean;
    public readonly scannable: boolean;
    public readonly readonly: boolean;
    public readonly unit: string;
    public readonly focusCursorPosition: number;
    public applicationIdentifier: ConfigConstants.ApplicationIdentifier;
    public readonly inputType: string;
    public readonly patternErrorMessage?: string;

    /**
     * Constructor that expects an object with some or all of the supported options set
     *
     * @param {number} [minLength]
     * @param {number} [maxLength]
     * @param {string} [placeholder]
     * @param {string} [pattern] String representing a regex pattern for validation
     * @param {string} [patternScan] String representing a regex pattern for barcode scanning
     * @param {boolean} [required] is this parameter mandatory?
     * @param {boolean} [scannable] should the barcode scanner be available
     * @param {boolean} [readonly] should the input field be readonly?
     * @param {boolean} [unit] unit label string
     * @param {number} [focusCursorPosition] Where should the cursor be positioned upon receiving focus on the input?
     * @param {ConfigConstants.ApplicationIdentifier} [applicationIdentifier] are numeric prefixes used in barcode scan to define the meaning and format of encoded data elements.
     * @param inputType
     * @param {string} [patternErrorMessage] translation key of a field specific message shown if provided, when the pattern check fails.
     */
    public constructor({
        minLength = 0,
        maxLength = Infinity,
        placeholder = '',
        pattern = '[\\s\\S]*',
        patternScan = '[\\s\\S]*',
        required = false,
        readonly = false,
        scannable = false,
        unit = '',
        focusCursorPosition = -1,
        applicationIdentifier = ConfigConstants.ApplicationIdentifier.Default,
        inputType = ConfigConstants.inputTypeText,
        patternErrorMessage,
    }: {
        minLength?: number;
        maxLength?: number;
        placeholder?: string;
        pattern?: string;
        patternScan?: string;
        required?: boolean;
        readonly?: boolean;
        scannable?: boolean;
        unit?: string;
        focusCursorPosition?: number;
        applicationIdentifier?: ConfigConstants.ApplicationIdentifier;
        inputType?: string;
        patternErrorMessage?: string;
    } = {}) {
        super();
        this.minLength = minLength;
        this.maxLength = maxLength;
        this.placeholder = placeholder;
        this.pattern = pattern;
        this.patternScan = patternScan;
        this.required = required;
        this.readonly = readonly;
        this.scannable = scannable;
        this.unit = unit;
        this.focusCursorPosition = focusCursorPosition;
        this.applicationIdentifier = applicationIdentifier;
        this.inputType = inputType;
        this.patternErrorMessage = patternErrorMessage;
    }
}

export class TextModelOptionsFromJson extends TextModelOptions {
    public constructor(data: JsonTextModelOptions) {
        super({
            minLength: data.minLength,
            maxLength: data.maxLength ? data.maxLength : Infinity,
            placeholder: data.placeholder,
            pattern: data.pattern,
            patternScan: data.patternScan,
            required: data.required,
            readonly: data.readonly,
            scannable: data.scannable,
            unit: data.unit,
            focusCursorPosition: data.focusCursorPosition,
            applicationIdentifier: data.applicationIdentifier,
            inputType: data.inputType ?? ConfigConstants.inputTypeText,
        });
    }
}

export interface JsonTextModelOptions {
    minLength: number;
    maxLength: number;
    placeholder: string;
    pattern: string;
    patternScan: string;
    required: boolean;
    scannable: boolean;
    readonly: boolean;
    visible: boolean;
    unit: string;
    focusCursorPosition: number;
    applicationIdentifier: ConfigConstants.ApplicationIdentifier;
    inputType: string;
}
